import { useState, useEffect } from 'react';
import { User, Users, UserCheck, UserX, Plus } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router';
import { useTranslation } from '../../i18n/i18n';
import ReusableTable from '../../components/Tableau';
import UserModal from '../../components/Modals/UserModal';
import api from '../../api/axios';

const UsersPage = ({ lang }) => {
  const { t } = useTranslation(lang);
  const navigate = useNavigate();
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'view', 'edit', 'add'
  const usersURL = process.env.REACT_APP_API_USERS_URL;
  const [users, setUsers] = useState([]);

  const fetchAllUsers = async () => {
    try {

      const urlBase = usersURL;

      // Définir le body correctement
      const baseRequestBody = {
        input_search: "",
        status: ""
      };

      // 1. Charger la page 1
      const firstResponse = await api.post(urlBase, baseRequestBody, {
        params: { page: 1 } 
      });

      const firstData = firstResponse.data;

      const lastPage = firstData.last_page;

      // Formater les données
      let allUsers = firstData.result.data.map(formatUser);

      // 2. Charger les autres pages EN PARALLÈLE
      const promises = [];

      for (let page = 2; page <= lastPage; page++) {
        promises.push(
          api.post(urlBase, baseRequestBody, {
            params: { page: page }
          })
            .then(res => res.data.result.data.map(formatUser))
        );
      }

      const pagesResults = await Promise.all(promises);

      pagesResults.forEach(page => {
        allUsers.push(...page);
      });

      // 3. Mettre à jour l'état
      setUsers(allUsers);

    } catch (err) {
      console.error("Erreur API détaillée:", err);
      if (err.response) {
        console.error("Status:", err.response.status);
        console.error("Data:", err.response.data);
        console.error("Headers:", err.response.headers);
      }
    } finally {
    }
  };

  // Fonction pour formater les données de l'API
  const formatUser = (user) => {
    return {
      id: user.id,
      name: user.name,
      email: user.email,
      phone: user.phone,
      status: user.status,
      created_at: user.created_at
    };
  };

  // Charger les fournisseurs au montage du composant
  useEffect(() => {
    fetchAllUsers();
  }, []);

  // Calculer le nombre de pays uniques
  const enabled = users.filter(user => user.status === 'enabled' ).length;
  const disabled = users.filter(user => user.status === 'disabled' ).length;
  // Cartes de statistiques
  const userStats = [
    {
      title: t('totalUsers'),
      value: users.length.toString(),
      description: t('allPlatformUsers'),
      icon: Users,
      color: '#3b82f6',
    },
    {
      title: t('enabledUsers'),
      value: enabled.toString(),
      description: t('activeUsers'),
      icon: UserCheck,
      color: '#10b981',
    },
    {
      title: t('disabledUsers'),
      value: disabled.toString(),
      description: t('inactiveUsers'),
      icon: UserX,
      color: '#ef4444',
    }
  ];

  // Colonnes du tableau
  const userColumns = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: t('name') },
    { key: 'email', label: t('email') },
    { key: 'status', label: t('status') },
    { key: 'phone', label: t('phone') },
    { key: 'created_at', label: t('createdAt') }
  ];

// Handlers pour la modal utilisateur
const handleAdd = async (userData) => {
  try {
    // Appel API pour ajouter un utilisateur
    const response = await api.post('/users/create', userData);
    
    if (response.data?.request?.status === false) {
      toast.error(response.data.request.message);
      return;
    }
    
    // Ajouter le nouvel utilisateur à la liste
    const newUser = {
      ...userData,
      id: response.data?.result?.id || Date.now()
    };
    
    setUsers(prev => [...prev, newUser]);
    toast.success(t('userAddedSuccess'));
    setShowUserModal(false);
    
  } catch (error) {
    console.error('Erreur lors de l\'ajout:', error);
    
    // Gestion d'erreur
    let errorMessage = t('addUserError');
    if (error.response?.data?.request?.message) {
      errorMessage = error.response.data.request.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }
    
    toast.error(errorMessage);
  }
};

// const handleEdit = async (userData) => {
//   try {
//     if (!selectedUser?.id) {
//       toast.error(t('noUserSelected'));
//       return;
//     }
    
//     // Appel API pour modifier un utilisateur
//     const response = await api.post(`/users/${selectedUser.id}/update`, userData);
    
//     if (response.data?.request?.status === false) {
//       toast.error(response.data.request.message);
//       return;
//     }
    
//     // Mettre à jour l'utilisateur dans la liste
//     setUsers(prev => 
//       prev.map(u => 
//         u.id === selectedUser.id 
//           ? { ...u, ...userData, updatedAt: new Date().toISOString() }
//           : u
//       )
//     );
    
//     toast.success(t('userUpdatedSuccess'));
//     setShowUserModal(false);
    
//   } catch (error) {
//     console.error('Erreur lors de la modification:', error);
    
//     // Gestion d'erreur
//     let errorMessage = t('editUserError');
//     if (error.response?.data?.request?.message) {
//       errorMessage = error.response.data.request.message;
//     } else if (error.response?.data?.message) {
//       errorMessage = error.response.data.message;
//     }
    
//     toast.error(errorMessage);
//   }
// };

  // const handleEditUser = (user) => {
  //   setSelectedUser(user);
  //   setModalType('edit');
  //   setShowUserModal(true);
  // };

  const handleDelete = async (user) => {
    const result = await Swal.fire({
      title: t('confirmDeleteTitle'),
      text: t('confirmDeleteText', { name: user.name }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: t('deleteConfirm'),
      cancelButtonText: t('cancel')
    });

    if (result.isConfirmed) {
      setUsers(users.filter(u => u.id !== user.id));
      toast.success(t('userDeletedSuccess'));
    }
  };

  const handleDetails = (user) => {
    setSelectedUser(user);
    navigate(`/users/${user.id}`);
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setModalType('add');
    setShowUserModal(true);
  };

  // Nouveau handler pour le click sur la ligne
  const handleRowClick = (user) => {
    handleDetails(user);
  };

  const styles = {
    container: {
      padding: '24px',
      background: '#f9fafb',
      minHeight: '100vh'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '32px'
    },
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0
    },
    addButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px 20px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      transition: 'all 0.2s ease'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '15px',
      marginBottom: '32px'
    },
    statCard: {
      background: 'white',
      padding: '20px',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
    },
    statIcon: {
      width: '56px',
      height: '56px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    statContent: {
      flex: 1
    },
    statValue: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: '4px'
    },
    statTitle: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '4px',
      fontWeight: '600'
    },
    statDescription: {
      fontSize: '12px',
      color: '#9ca3af'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header avec titre et bouton */}
      <div style={styles.header}>
        <div style={styles.titleContainer}>
          <User size={32} color="#3b82f6" />
          <h1 style={styles.title}>{t('usersManagement')}</h1>
        </div>
        <button
          style={styles.addButton}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#1b82f6'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
          onClick={handleAddUser}
        >
          <Plus size={20} />
          {t('addUser')}
        </button>
      </div>

      {/* Cartes de statistiques */}
      <div style={styles.statsGrid}>
        {userStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              style={styles.statCard}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
              }}
            >
              <div style={{
                ...styles.statIcon,
                background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}dd 100%)`
              }}>
                <Icon size={28} color="white" />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{stat.value}</div>
                <div style={styles.statTitle}>{stat.title}</div>
                <div style={styles.statDescription}>{stat.description}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tableau des utilisateurs */}
      <ReusableTable
        data={users}
        columns={userColumns}
        // onEdit={handleEditUser}
        onDelete={handleDelete}
        onDetails={handleDetails}
        onRowClick={handleRowClick}
        lang={lang}
        itemsPerPageOptions={[5, 10, 20, 50]}
        colors={{
          headerBg: '#3b82f6',
          buttonEdit: '#5995f7ff',
          buttonDelete: '#ee6565ff',
          buttonDetails: '#3bb48cff',
          paginatebg: '#ddeafdff'
        }}
      />

      {showUserModal && (
        <UserModal
          user={selectedUser}
          type={modalType}
          onClose={() => setShowUserModal(false)}
          onSave={(userData) => {
            // Logique de sauvegarde
            if (modalType === 'add') {
              handleAdd(userData);
            } 
            // else{
            //   handleEdit(userData);
            // }
            setShowUserModal(false);
          }} 
          lang={lang}
        />
      )}

      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default UsersPage;